import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { FaCalendarAlt, FaCheck, FaTimes, FaUserClock } from "react-icons/fa";
import BlobImage from "../../../../components/BlobImage/BlobImage";
import dayjs from "dayjs";
import styles from "../Create.module.css";
import cardStyles from "../Attendancecard.module.css";
import AxiosInstance, {
  IMAGE_BASE_URL,
  TokenService,
} from "../../../../services/Api";
import { CustomButton, CustomDropdown } from "../../../../components";
import {
  fetchSectionOptions,
  fetchStaffClassAndSubjects,
  clearSectionOptions,
} from "../../../../redux/Masterdata";
import moment from "moment";

const ManageAttendanceCreate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  // Check if editing (coming from attendance list with state)
  const editData = location.state?.data || null;
  const isEditMode = !!editData;

  // Today's date
  const today = dayjs();
  const todayFormatted = today.format("DD-MM-YYYY");
  const todayDay = today.format("dddd");

  // Get user data from TokenService
  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const client_id = clientData?.client_id || "";
  const school_id = clientData?.school_id || "";
  const staff_id = clientData?.master_id || "";

  const { translations } = useSelector((state) => state.language);

  // Get data from masterData redux slice
  const {
    staffClassAndSubjectOptions: reduxClassOptions,
    staffClassAndSubjectLoading: classLoading,
    sectionOptions: reduxSectionOptions,
    sectionLoading,
  } = useSelector((state) => state.masterData);
  const [leaveData, setLeaveData] = useState("");
  const [formData, setFormData] = useState({
    classId: editData?.class_id || "",
    sectionId: editData?.section_id || "",
    attendanceDate: editData?.attendance_date
      ? moment(editData.attendance_date, "DD/MM/YYYY").format("YYYY-MM-DD")
      : moment(new Date()).format("YYYY-MM-DD"),
  });

  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Build class dropdown options (deduplicated)
  const classOptions = useMemo(() => {
    const seen = new Set();
    return (reduxClassOptions || [])
      .filter((item) => {
        if (seen.has(item.class_id)) return false;
        seen.add(item.class_id);
        return true;
      })
      .map((item) => ({
        value: item.class_id,
        label: item.class_name,
      }));
  }, [reduxClassOptions]);

  // Build section dropdown options from redux
  const sectionOptions = useMemo(
    () => reduxSectionOptions || [],
    [reduxSectionOptions],
  );

  // Calculate stats from students array
  const stats = useMemo(() => {
    const total = students.length;
    const presentCount = students.filter(
      (s) => s.attendance_status === 1,
    ).length;
    const absentCount = students.filter(
      (s) => s.attendance_status === 0,
    ).length;
    const leaveCount = students.filter((s) => s.student_leaveid > 0).length;
    return {
      classStrength: total,
      present: presentCount,
      absent: absentCount,
      leave: leaveCount,
    };
  }, [students]);

  // Fetch staff class list on mount
  useEffect(() => {
    if (staff_id) {
      dispatch(
        fetchStaffClassAndSubjects({
          staff_id: parseInt(staff_id),
          class_id: "",
          section_id: "",
          subject_id: "",
        }),
      );
    }
  }, [dispatch, staff_id]);

  // Fetch section options when class changes
  useEffect(() => {
    if (formData.classId) {
      dispatch(
        fetchSectionOptions({
          id: [parseInt(formData.classId)],
          school_id: parseInt(school_id),
          staff_id: parseInt(staff_id),
        }),
      );
    } else {
      dispatch(clearSectionOptions());
    }
  }, [dispatch, formData.classId, school_id, staff_id]);

  // In edit mode, fetch view attendance data; in create mode, fetch student list
  useEffect(() => {
    if (
      isEditMode &&
      formData.classId &&
      formData.sectionId &&
      formData.attendanceDate
    ) {
      fetchViewAttendance();
      fetchStudentLeaveCount(formData.sectionId, "update");
    } else if (!isEditMode && formData.classId && formData.sectionId) {
      fetchStudentLeaveCount(formData.sectionId);
    }
  }, [
    formData.classId,
    formData.sectionId,
    formData.attendanceDate,
    isEditMode,
  ]);

  // Fetch student list for CREATE mode
  const fetchStudentList = async (leaveStudents) => {
    setStudentsLoading(true);
    try {
      const payload = {
        staff_id: parseInt(staff_id),
        class_id: String(formData.classId),
        section: formData.sectionId,
        page: "",
        limit: "",
      };

      const response = await AxiosInstance.post(
        `/school/staff/studentList`,
        payload,
      );

      if (response?.data?.status) {
        const leaveStudentIds = leaveStudents.map((leave) => leave.student_id);

        const studentData = response.data.data || [];

        const mappedStudents = studentData.map((item) => {
          const isLeaveStudent = leaveStudentIds.includes(item.stud_id);
          const leaveRow = isLeaveStudent
            ? leaveStudents.find((leave) => leave.student_id === item.stud_id)
            : null;

          return {
            stud_id: item.user_id,
            student_id: item.student_id,
            name: item.name,
            original_path: item.original_path,
            student_photo_path: item.student_photo_path,
            attendance_status: isLeaveStudent ? 2 : 1,
            student_leaveid: leaveRow?.leave_id || 0,
          };
        });

        setStudents(mappedStudents);
      } else {
        setStudents([]);
      }
    } catch (error) {
      console.error("Error fetching student list:", error);
      setStudents([]);
    } finally {
      setStudentsLoading(false);
    }
  };

  const fetchStudentLeaveCount = async (value, mode) => {
    setStudentsLoading(true);
    try {
      const payload = {
        school_id: school_id,
        class_id: String(formData.classId),
        sec_id: formData.sectionId,
        date: moment(todayFormatted, "DD-MM-YYYY").format("YYYY-MM-DD"),
      };

      const response = await AxiosInstance.post(
        `/attendance/leave_student_count`,
        payload,
      );

      if (response?.data?.status) {
        const totalStudents = response?.data?.total_students ?? 0;
        const students = response?.data?.data ?? [];

        setLeaveData(totalStudents);
        if (mode !== "update") {
          fetchStudentList(students);
        }
      } else {
        console.log("error");
      }
    } catch (error) {
      console.error("Error fetching student list:", error);
      setStudents([]);
    } finally {
      setStudentsLoading(false);
    }
  };

  // Fetch view attendance for EDIT mode
  const fetchViewAttendance = async () => {
    setStudentsLoading(true);
    try {
      const attendanceDate = editData?.attendance_date
        ? moment(editData.attendance_date, "DD/MM/YYYY").format("YYYY-MM-DD")
        : moment(new Date()).format("YYYY-MM-DD");

      // use attendanceDate in API call
      const payload = {
        staff_id: parseInt(staff_id),
        class_id: parseInt(formData.classId),
        section_id: formData.sectionId,
        attendance_date: attendanceDate,
      };

      const response = await AxiosInstance.post(
        `/attendance/viewAttendance`,
        payload,
      );

      if (response?.data?.status) {
        const attendanceData = response.data.data || [];
        const mappedStudents = attendanceData.map((item) => ({
          stud_id: item.master_id,
          student_id: item.student_id,
          name: item.name,
          original_path: item.original_path,
          student_photo_path: item.student_photo_path,
          attendance_status: item.attendance_status,
          student_leaveid: item.student_leaveid || 0,
          attendance_id: item.attendance_id,
        }));
        setStudents(mappedStudents);
      } else {
        setStudents([]);
      }
    } catch (error) {
      console.error("Error fetching attendance view:", error);
      setStudents([]);
    } finally {
      setStudentsLoading(false);
    }
  };

  // Toggle attendance status for a student (leave students stay leave)
  const toggleAttendance = useCallback((studentStudId) => {
    setStudents((prev) =>
      prev.map((student) =>
        student.stud_id === studentStudId && student.attendance_status !== 2
          ? {
              ...student,
              attendance_status: student.attendance_status === 1 ? 0 : 1,
            }
          : student,
      ),
    );
  }, []);

  // ==================== LEAVE DETAILS POPUP ====================
  const [leaveModal, setLeaveModal] = useState({
    open: false,
    loading: false,
    studentName: "",
    data: null,
  });

  const closeLeaveModal = () =>
    setLeaveModal({ open: false, loading: false, studentName: "", data: null });

  const openLeaveDetails = async (student) => {
    setLeaveModal({
      open: true,
      loading: true,
      studentName: student.name,
      data: null,
    });
    try {
      // Attendance date for the fallback lookup (older rows have no leave id)
      const attendanceDate =
        isEditMode && editData?.attendance_date
          ? moment(editData.attendance_date, "DD/MM/YYYY").format("YYYY-MM-DD")
          : moment(new Date()).format("YYYY-MM-DD");

      const response = await AxiosInstance.post(`/attendance/viewLeave`, {
        id: student.student_leaveid || 0,
        student_user_id: student.stud_id,
        date: attendanceDate,
      });
      if (response?.data?.status && response?.data?.data) {
        setLeaveModal((prev) => ({
          ...prev,
          loading: false,
          data: response.data.data,
        }));
        return;
      }
      setLeaveModal((prev) => ({ ...prev, loading: false, data: null }));
    } catch (error) {
      console.error("Error fetching leave details:", error);
      setLeaveModal((prev) => ({ ...prev, loading: false, data: null }));
    }
  };

  // Handle dropdown changes
  const handleDropdownChange = (name, selectedOption) => {
    const value = selectedOption?.value ?? selectedOption ?? "";

    if (name === "classId") {
      setFormData((prev) => ({
        ...prev,
        classId: value,
        sectionId: "",
      }));
      setStudents([]);
      setErrors({});
    } else if (name === "sectionId") {
      // fetchStudentLeaveCount(value);
      setFormData((prev) => ({
        ...prev,
        sectionId: value,
      }));
      setStudents([]);
      setErrors({});
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    if (!formData.classId) {
      newErrors.classId =
        translations.labels?.classRequired || "Class is required";
    }
    if (!formData.sectionId) {
      newErrors.sectionId =
        translations.labels?.sectionRequired || "Section is required";
    }
    if (students.length === 0) {
      newErrors.students =
        translations.labels?.noStudentsFound || "No students found";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle Save (Create or Update)
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const studentsPayload = students.map((s) => ({
        student_id: s.stud_id,
        attendance_status: s.attendance_status,
        student_leaveid: s.student_leaveid,
      }));

      if (isEditMode) {
        // UPDATE
        const payload = {
          staff_id: parseInt(staff_id),
          class_id: parseInt(formData.classId),
          section_id: formData.sectionId,
          attendance_date: dayjs(formData.attendanceDate).format("YYYY-MM-DD"),
          updated_by: parseInt(staff_id),
          students: studentsPayload,
        };

        await AxiosInstance.put(`/attendance/updateAttendance`, payload);
      } else {
        // CREATE
        const payload = {
          client_id: parseInt(client_id),
          school_id: parseInt(school_id),
          class_id: parseInt(formData.classId),
          section_id: formData.sectionId,
          shift_id: 1,
          period: 1,
          attendance_date: dayjs(formData.attendanceDate).format("YYYY-MM-DD"),
          created_by: parseInt(staff_id),
          students: studentsPayload,
        };

        await AxiosInstance.post(`/attendance/createAttendance`, payload);
      }

      navigate("/user/attendanceList");
    } catch (error) {
      console.error("Error saving attendance:", error);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle Cancel
  const handleCancel = () => {
    navigate("/user/attendanceList");
  };

  // Get student photo URL or return null
  const getStudentPhoto = (student) => {
    if (student.student_photo_path) return student.student_photo_path;
    if (student.original_path) return student.original_path;
    return null;
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <FaCalendarAlt />
        </div>
        <h1 className={styles.headerTitle}>
          {isEditMode
            ? translations.labels?.editAttendance || "Edit Attendance"
            : translations.labels?.createAttendance || "Create Attendance"}
        </h1>
      </div>

      {/* Date & Day Header */}
      <div className={cardStyles.dateHeader}>
        <h2 className={cardStyles.dateText}>
          {isEditMode && editData?.attendance_date
            ? `${editData.attendance_date} | ${todayDay}`
            : `${todayFormatted} | ${todayDay}`}
        </h2>
      </div>

      {/* Filters - Class & Section only */}
      <div className={styles.filterSection}>
        <div className="row align-items-end">
          {/* Class Dropdown */}
          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomDropdown
              label={translations.labels?.class || "Class"}
              name="classId"
              value={formData.classId}
              onChange={(value) => handleDropdownChange("classId", value)}
              options={classOptions}
              placeholder={
                translations.labels?.classPlaceholder || "Select Class"
              }
              loading={classLoading}
              disabled={isEditMode}
              required
              error={errors.classId}
            />
          </div>

          {/* Section Dropdown */}
          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomDropdown
              label={translations.labels?.section || "Section"}
              name="sectionId"
              value={formData.sectionId}
              onChange={(value) => handleDropdownChange("sectionId", value)}
              options={sectionOptions}
              placeholder={
                translations.labels?.sectionPlaceholder || "Select Section"
              }
              disabled={!formData.classId || isEditMode}
              loading={sectionLoading}
              required
              error={errors.sectionId}
            />
          </div>
        </div>
      </div>

      {/* Stats Row */}
      {students.length > 0 && (
        <div className={cardStyles.statsRow}>
          <div className={cardStyles.statItem}>
            <span className={cardStyles.statLabel}>
              {translations.labels?.classStrength || "Class Strength"}
            </span>
            <span className={cardStyles.statSeparator}>:</span>
            <span className={cardStyles.statValue}>{stats.classStrength}</span>
          </div>
          <div className={cardStyles.statItem}>
            <span className={cardStyles.statLabel}>
              {translations.labels?.leave || "Leave"}
            </span>
            <span className={cardStyles.statSeparator}>:</span>
            <span className={cardStyles.statValue}>{leaveData}</span>
          </div>
          <div className={cardStyles.statItem}>
            <span className={cardStyles.statLabel}>
              {translations.labels?.present || "Present"}
            </span>
            <span className={cardStyles.statSeparator}>:</span>
            <span className={cardStyles.statValue}>{stats.present}</span>
          </div>
          <div className={cardStyles.statItem}>
            <span className={cardStyles.statLabel}>
              {translations.labels?.absent || "Absent"}
            </span>
            <span className={cardStyles.statSeparator}>:</span>
            <span className={cardStyles.statValue}>{stats.absent}</span>
          </div>
        </div>
      )}

      {/* Student Cards Grid */}
      {studentsLoading ? (
        <div className={styles.loadingContainer}>
          <p>{translations.labels?.loading || "Loading..."}</p>
        </div>
      ) : students.length > 0 ? (
        <>
          <div className={cardStyles.studentGrid}>
            {students.map((student) => {
              const isPresent = student.attendance_status === 1;
              const isLeave = student.attendance_status === 2;
              const photoUrl = getStudentPhoto(student);
              return (
                <div
                  key={student.stud_id}
                  className={`${cardStyles.studentCard} ${
                    isLeave
                      ? cardStyles.studentCardLeave
                      : isPresent
                        ? cardStyles.studentCardPresent
                        : cardStyles.studentCardAbsent
                  }`}
                  onClick={() =>
                    isLeave
                      ? openLeaveDetails(student)
                      : toggleAttendance(student.stud_id)
                  }
                >
                  {/* Avatar */}
                  <div className={cardStyles.avatarWrapper}>
                    {photoUrl ? (
                      <BlobImage
                        path={photoUrl}
                        alt={student.name}
                        className={cardStyles.avatarImage}
                      />
                    ) : (
                      <div className={cardStyles.avatarPlaceholder}>
                        <svg
                          viewBox="0 0 40 40"
                          className={cardStyles.avatarSvg}
                        >
                          <circle cx="20" cy="20" r="20" fill="#e0e0e0" />
                          <circle cx="20" cy="15" r="7" fill="#f5a623" />
                          <ellipse
                            cx="20"
                            cy="32"
                            rx="12"
                            ry="10"
                            fill="#f5a623"
                          />
                        </svg>
                      </div>
                    )}
                  </div>

                  {/* Student Info */}
                  <div className={cardStyles.studentInfo}>
                    <span className={cardStyles.studentId}>
                      {student.student_id}
                    </span>
                    <span className={cardStyles.studentName}>
                      {student.name}
                    </span>
                  </div>

                  {/* Status Icon */}
                  <div
                    className={`${cardStyles.statusIcon} ${
                      isLeave
                        ? cardStyles.statusLeave
                        : isPresent
                          ? cardStyles.statusPresent
                          : cardStyles.statusAbsent
                    }`}
                    title={
                      isLeave
                        ? translations.labels?.leaveDetails || "Leave details"
                        : ""
                    }
                  >
                    {isLeave ? (
                      <FaUserClock />
                    ) : isPresent ? (
                      <FaCheck />
                    ) : (
                      <FaTimes />
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Error for students */}
          {errors.students && (
            <p className={styles.errorText}>{errors.students}</p>
          )}

          {/* Buttons */}
          <div className={styles.buttonContainer}>
            <CustomButton
              type="button"
              variant="contained"
              className={styles.submitButton}
              fullWidth={false}
              onClick={handleSubmit}
              loading={submitting}
              loadingText={
                isEditMode
                  ? translations.labels?.updating || "Updating..."
                  : translations.labels?.saving || "Saving..."
              }
            >
              {translations.labels?.saveButton || "SAVE"}
            </CustomButton>

            <CustomButton
              type="button"
              variant="contained"
              className={styles.cancelButton}
              fullWidth={false}
              onClick={handleCancel}
            >
              {translations.labels?.cancelButton || "CANCEL"}
            </CustomButton>
          </div>
        </>
      ) : (
        formData.classId &&
        formData.sectionId && (
          <div className={styles.loadingContainer}>
            <p>{translations.labels?.noStudentsFound || "No students found"}</p>
          </div>
        )
      )}

      {/* ==================== LEAVE DETAILS POPUP ==================== */}
      {leaveModal.open && (
        <div
          onClick={closeLeaveModal}
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1300,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: "#fff",
              borderRadius: 8,
              padding: "20px 24px",
              width: 380,
              maxWidth: "90vw",
              boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 12,
              }}
            >
              <h4 style={{ margin: 0, fontSize: 16, color: "#2f80ed" }}>
                <FaUserClock style={{ marginRight: 8 }} />
                {translations.labels?.leaveDetails || "Leave Details"}
              </h4>
              <FaTimes
                onClick={closeLeaveModal}
                style={{ cursor: "pointer", color: "#888" }}
              />
            </div>

            {leaveModal.loading ? (
              <p style={{ margin: "12px 0" }}>
                {translations.labels?.loading || "Loading..."}
              </p>
            ) : leaveModal.data ? (
              <div style={{ fontSize: 14 }}>
                {[
                  [
                    translations.labels?.studentName || "Student",
                    leaveModal.data.student_name || leaveModal.studentName,
                  ],
                  [
                    translations.labels?.fromDate || "From Date",
                    leaveModal.data.from_date
                      ? moment(leaveModal.data.from_date).format("DD-MM-YYYY")
                      : "-",
                  ],
                  [
                    translations.labels?.toDate || "To Date",
                    leaveModal.data.to_date
                      ? moment(leaveModal.data.to_date).format("DD-MM-YYYY")
                      : "-",
                  ],
                  [
                    translations.labels?.noOfDays || "No. of Days",
                    leaveModal.data.no_days ?? "-",
                  ],
                  [
                    translations.labels?.reason || "Reason",
                    leaveModal.data.reason || "-",
                  ],
                  [
                    translations.labels?.status || "Status",
                    Number(leaveModal.data.status) === 1
                      ? translations.labels?.approved || "Approved"
                      : leaveModal.data.status,
                  ],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      padding: "6px 0",
                    }}
                  >
                    <span
                      style={{
                        width: 120,
                        flexShrink: 0,
                        color: "#777",
                      }}
                    >
                      {label}
                    </span>
                    <span
                      style={{
                        flex: 1,
                        color: "#333",
                        fontWeight: 500,
                        wordBreak: "break-word",
                      }}
                    >
                      {value}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ margin: "12px 0" }}>
                {leaveModal.studentName} —{" "}
                {translations.labels?.leaveDetailsNotFound ||
                  "Leave details not available"}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageAttendanceCreate;
